import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const STATUS_OPTIONS = [
  { value: 'For Review', label: 'For Review' },
  { value: 'Approved', label: 'Approved' },
  { value: 'Failed', label: 'Failed' },
  { value: 'Excluded', label: 'Excluded' },
  { value: 'Suggest Approve', label: 'Suggest Approve' },
  { value: 'Suggest Exclude', label: 'Suggest Exclude' },
  { value: 'Suggest Fail', label: 'Suggest Fail' },
];

const REASON_OPTIONS = [
  'Management approval',
  'AI false response',
  'No longer in business',
];

const DECISION_LABELS = {
  for_review: 'For Review',
  approved: 'Approved',
  failed: 'Failed',
  excluded: 'Excluded',
  suggest_approve: 'Suggest Approve',
  suggest_exclude: 'Suggest Exclude',
  suggest_fail: 'Suggest Fail',
};

const DECISION_COLORS = {
  for_review:      'bg-[#FFA500] text-white',
  approved:        'bg-[#2C853C] text-white',
  failed:          'bg-[#DC143C] text-white',
  excluded:        'bg-[#4169E1] text-white',
  suggest_approve: 'bg-[#6D4694] text-white',
  suggest_exclude: 'bg-[#6D4694] text-white',
  suggest_fail:    'bg-[#6D4694] text-white',
};

// Normalise decision string to the snake_case key used in DECISION_COLORS/LABELS
const toKey = (val) => val?.toLowerCase().replace(/ /g, '_');

export const PolicyStatusModal = ({ policy, onClose, onUpdate, onFetchHistory }) => {
  const [selectedStatus, setSelectedStatus] = useState(policy.decision ?? '');
  const [selectedReason, setSelectedReason] = useState('');
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  useEffect(() => {
    if (!onFetchHistory) { setHistoryLoading(false); return; }
    onFetchHistory(policy.ppf_id)
      .then(data => setHistory(Array.isArray(data) ? data : []))
      .catch(() => setHistory([]))
      .finally(() => setHistoryLoading(false));
  }, []);

  const canSubmit = selectedStatus && selectedStatus !== policy.decision && selectedReason && comment.trim();

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setLoading(true);
    try {
      await onUpdate(policy.id, { status: selectedStatus, reasons: [selectedReason], comment: comment.trim() });
      onClose();
    } catch {
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
          <h2 className="text-base font-semibold text-gray-900">Update Policy Status</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-5">

          {/* Policy identifier + current decision */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Policy:{' '}
              <span className="font-medium text-gray-900 font-mono text-xs">
                {policy.ppf_file_name ?? policy.policyId ?? policy.policy}
              </span>
            </p>
            {policy.decision && (
              <span className={`text-xs px-2 py-0.5 rounded font-medium ${DECISION_COLORS[toKey(policy.decision)] ?? 'bg-gray-100 text-gray-700'}`}>
                Current: {policy.decision}
              </span>
            )}
          </div>

          <hr className="border-gray-200" />

          {/* Change Status */}
          <div>
            <p className="text-sm font-semibold text-gray-900 mb-2">
              Change Status to: <span className="text-red-500">*</span>
            </p>
            <div className="grid grid-cols-2 gap-2">
              {STATUS_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  disabled={opt.value === policy.decision}
                  onClick={() => setSelectedStatus(opt.value)}
                  className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors text-left ${
                    opt.value === policy.decision
                      ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                      : selectedStatus === opt.value
                        ? 'border-blue-600 bg-blue-50 text-blue-700'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                  }`}
                >
                  {opt.label}{opt.value === policy.decision ? ' (current)' : ''}
                </button>
              ))}
            </div>
          </div>

          {/* Reasons */}
          <div>
            <p className="text-sm font-semibold text-gray-900 mb-2">
              Reason: <span className="text-red-500">*</span>
            </p>
            <div className="space-y-2">
              {REASON_OPTIONS.map(reason => (
                <label key={reason} className="flex items-center gap-2.5 cursor-pointer">
                  <input
                    type="radio"
                    name="reason"
                    value={reason}
                    checked={selectedReason === reason}
                    onChange={() => setSelectedReason(reason)}
                    className="w-4 h-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">{reason}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Comment */}
          <div>
            <p className="text-sm font-semibold text-gray-900 mb-2">
              Comment: <span className="text-red-500">*</span>
            </p>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              placeholder="Add a comment..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          {/* Audit History */}
          <div>
            <p className="text-sm font-semibold text-gray-900 mb-3">Update History:</p>
            {historyLoading ? (
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                Loading history...
              </div>
            ) : history.length === 0 ? (
              <p className="text-sm text-gray-400">No history found.</p>
            ) : (
              <div className="space-y-3">
                {history.map((entry) => (
                  <div key={entry.id} className="border-l-2 border-gray-200 pl-3 py-0.5">
                    <p className="text-xs text-gray-500 mb-1.5">
                      <span className="font-medium text-gray-700">{entry.requested_by ?? '—'}</span>
                      {' — '}
                      {entry.date ? new Date(entry.date).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                    </p>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${DECISION_COLORS[toKey(entry.before_value)] ?? 'bg-gray-100 text-gray-700'}`}>
                        {entry.before_value ?? '—'}
                      </span>
                      <span className="text-xs text-gray-400">→</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${DECISION_COLORS[toKey(entry.after_value)] ?? 'bg-gray-100 text-gray-700'}`}>
                        {entry.after_value ?? '—'}
                      </span>
                    </div>
                    {entry.reason && (
                      <p className="text-xs text-gray-500 mt-1">{entry.reason}</p>
                    )}
                    {entry.comment && (
                      <p className="text-xs text-gray-600 mt-0.5 italic">"{entry.comment}"</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || loading}
            className={`px-4 py-2 text-sm font-medium rounded-lg text-white transition-colors ${
              canSubmit && !loading
                ? 'bg-blue-600 hover:bg-blue-700 cursor-pointer'
                : 'bg-blue-300 cursor-not-allowed opacity-60'
            }`}
          >
            {loading ? 'Updating...' : 'Update Status'}
          </button>
        </div>
      </div>
    </div>
  );
};
